/*
  INAYAH — one-click upsell add control.
  Pairs with snippets/inayah-oneclick-add.liquid. Resolves the variant from the
  selected color + size and adds it to the cart in one click using the theme's
  native add path (routes.cart_add_url) + cart-drawer renderContents() refresh —
  no "Choose options" modal. Globals fetchConfig / routes / updateCartCounters
  come from global.js (loaded site-wide).
*/
if (!customElements.get('inayah-oneclick-add')) {
  class InayahOneClickAdd extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.sizePos = parseInt(this.dataset.sizePos, 10) || 0;
      this.colorPos = parseInt(this.dataset.colorPos, 10) || 0;
      this.variants = this.readVariants();
      this.colorBtns = Array.from(this.querySelectorAll('.inayah-oc__color'));
      this.sizeSelect = this.querySelector('.inayah-oc__size');
      this.addBtn = this.querySelector('.inayah-oc__add');
      this.errorEl = this.querySelector('.inayah-oc__error');
      if (!this.addBtn || !this.variants.length) return;

      // The card this control belongs to (used to read the externally-rendered color
      // swatch when this control hides its own — the Dissh card layout).
      this.card =
        this.closest('product-card') ||
        this.closest('.product-card') ||
        this.closest('.product-recommendations__item');
      // No own color buttons but the product HAS a color option => the shopper picks
      // the color from the card's native swatch (beside the price), not from here.
      this.usesExternalColor = this.colorPos > 0 && this.colorBtns.length === 0;

      const initial =
        this.variants.find(v => String(v.id) === String(this.dataset.initialVariant)) ||
        this.variants.find(v => v.available) ||
        this.variants[0];
      if (this.colorPos > 0) {
        this.selectedColor = this.usesExternalColor
          ? this.getExternalColor() || (initial ? initial.color : null)
          : initial ? initial.color : null;
      } else {
        this.selectedColor = null;
      }

      // Prefer a specific colour passed from Liquid (the "also in <other colour>" cross-sell) when
      // this control owns its swatches — so the pinned card opens already on the un-owned colourway.
      if (this.colorPos > 0 && !this.usesExternalColor && this.dataset.preselectColor) {
        const wantColor = this.norm(this.dataset.preselectColor);
        const cm = this.variants.find(v => this.norm(v.color) === wantColor);
        if (cm) this.selectedColor = cm.color;
      }

      if (this.sizeSelect && initial && this.sizePos > 0) this.sizeSelect.value = initial.size;

      // Prefer the size the shopper already has in their cart (passed from Liquid as
      // data-preselect-size) when this product offers it, so the upsell defaults to their size.
      // refreshSizes() below still greys out / falls back if that size is sold out for the colour.
      if (this.sizeSelect && this.sizePos > 0 && this.dataset.preselectSize) {
        const want = this.norm(this.dataset.preselectSize);
        const match = Array.from(this.sizeSelect.options).find(o => this.norm(o.value) === want);
        if (match) this.sizeSelect.value = match.value;
      }

      this.bind();
      this.syncColorUI();
      this.refreshSizes();
      this.updateVariant();
      // In external-color mode the native swatch + card-variant-swap own the image.
      if (!this.usesExternalColor) this.swapImage(); // shown image matches pre-selected color
    }

    readVariants() {
      const node = this.querySelector('.inayah-oc__data');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return [];
      }
    }

    norm(s) {
      return (s == null ? '' : String(s)).toLowerCase().replace(/[\s\-–—]/g, '');
    }

    // Purchasable only via oversell policy => sells as pre-order.
    isPreorder(v) {
      if (!v || !v.available) return false;
      return !!(v.pre || (typeof v.qty === 'number' && v.qty <= 0 && v.policy === 'continue'));
    }

    variantFor(color, size) {
      return this.variants.find(v => {
        const colorOk = this.colorPos > 0 && color != null ? this.norm(v.color) === this.norm(color) : true;
        const sizeOk = this.sizePos > 0 && size != null ? this.norm(v.size) === this.norm(size) : true;
        return colorOk && sizeOk;
      });
    }

    // Read the color selected on the card's native swatch (Dissh layout), matching
    // against this product's known colors so a size radio is never mistaken for one.
    getExternalColor() {
      if (!this.card) return null;
      const colors = new Set(this.variants.map(v => this.norm(v.color)));
      let found = null;
      this.card.querySelectorAll('.variant-option-radio-input').forEach(r => {
        if (found) return;
        if ((r.checked || r.hasAttribute('checked')) && colors.has(this.norm(r.value))) {
          found = r.value;
        }
      });
      return found;
    }

    bind() {
      this.colorBtns.forEach(btn => {
        btn.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          this.selectedColor = btn.dataset.color;
          this.syncColorUI();
          this.refreshSizes();
          this.updateVariant();
          this.swapImage();
        });
      });
      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', () => this.updateVariant());
        // stop the select from bubbling into the card link
        this.sizeSelect.addEventListener('click', e => e.stopPropagation());
      }
      this.addBtn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.add();
      });

      // Dissh card layout: this control has no own swatches, so follow the color the
      // shopper picks on the card's native swatch — keep size availability + the Add
      // target variant in sync with it.
      if (this.usesExternalColor && this.card) {
        const resync = () => {
          // In the quick-add sheet the shopper picks colour from the SHEET swatches (selectColor),
          // which is authoritative — don't let the card's native swatch (still on its default) clobber it.
          if (this.classList.contains('is-sheet')) return;
          const c = this.getExternalColor();
          if (c && this.norm(c) !== this.norm(this.selectedColor)) {
            this.selectedColor = c;
            this.refreshSizes();
            this.updateVariant();
          }
        };
        // global.js flips the checked swatch on click/change; defer a tick so we read
        // the updated state.
        this.card.addEventListener('change', () => setTimeout(resync, 0));
        this.card.addEventListener('click', e => {
          if (e.target.closest && e.target.closest('.button--variant, .variant-option-radio-input')) {
            setTimeout(resync, 0);
          }
        });
        setTimeout(resync, 0); // initial sync after the card sets its default swatch
      }
    }

    syncColorUI() {
      this.colorBtns.forEach(b =>
        b.classList.toggle('is-selected', b.dataset.color === this.selectedColor)
      );
    }

    // Select a colour from the SHEET swatches (which aren't this control's own colorBtns and may
    // have no native card swatch to proxy). Updates colour + size availability + the add-to-bag
    // variant + the sheet image, so everything in the popup matches the chosen colourway.
    selectColor(color) {
      if (color == null || color === '') return;
      this.selectedColor = color;
      this.syncColorUI();
      this.refreshSizes();
      this.updateVariant();
      this.swapImage();
    }

    // Update the card's image to the selected color's variant image.
    swapImage() {
      if (!this.selectedColor) return;
      const match = this.variants.find(
        v => v.image && this.norm(v.color) === this.norm(this.selectedColor)
      );
      if (!match || !match.image) return;
      const card =
        this.closest('.product-recommendations__item') ||
        this.closest('.product-card') ||
        this.closest('product-card');
      if (!card) return;
      // Set every image in the card media (main + any variant images) so the
      // visible one always reflects the color, regardless of the card's media markup.
      var imgs = card.querySelectorAll(
        '.product-recommendations__item-media img, .product-card__media img, .media img'
      );
      imgs.forEach(function (img) {
        // srcset (and lazy-load data-srcset) override src, so clear them or the
        // displayed image (currentSrc) won't change.
        img.removeAttribute('srcset');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
        img.src = match.image;
      });
    }

    // Grey out + label sizes with no available variant for the current color,
    // and move a now-invalid selection to the first available size.
    refreshSizes() {
      if (!this.sizeSelect) return;
      let firstAvail = null;
      Array.from(this.sizeSelect.options).forEach(opt => {
        if (opt.dataset.base === undefined) opt.dataset.base = opt.textContent.trim();
        const v = this.variantFor(this.selectedColor, opt.value);
        const avail = !!(v && v.available);
        opt.disabled = !avail;
        opt.textContent = !avail ? opt.dataset.base + ' — Sold out' : (this.isPreorder(v) ? opt.dataset.base + ' — Pre-order' : opt.dataset.base);
        if (avail && firstAvail === null) firstAvail = opt.value;
      });
      const cur = this.sizeSelect.selectedOptions[0];
      if ((!cur || cur.disabled) && firstAvail !== null) this.sizeSelect.value = firstAvail;
    }

    currentSize() {
      return this.sizeSelect ? this.sizeSelect.value : null;
    }

    updateVariant() {
      const v = this.variantFor(this.selectedColor, this.currentSize());
      if (v && v.available) {
        this.addBtn.dataset.variantId = v.id;
        this.addBtn.removeAttribute('disabled');
        this.addBtn.classList.remove('is-unavailable');
      } else {
        this.addBtn.dataset.variantId = v ? v.id : '';
        this.addBtn.setAttribute('disabled', 'disabled');
        this.addBtn.classList.add('is-unavailable');
      }
      this.updateSheetImage(v);
      this.updateViewLink(v);
    }

    // Quick-add sheet only: deep-link "View Product Details" to the SELECTED variant so the full
    // PDP opens on the chosen colour/size. No-op when there's no view link (inline control).
    updateViewLink(v) {
      const view = this.querySelector('.inayah-oc__view');
      if (!view) return;
      const base = view.dataset.baseUrl || view.getAttribute('href') || '';
      if (!base) return;
      view.href = v ? base + (base.indexOf('?') === -1 ? '?' : '&') + 'variant=' + v.id : base;
    }

    // Quick-add sheet only: show the SELECTED colourway's photo. Falls back to any image for the
    // colour, then leaves the initial image. No-op when there's no sheet image (inline control).
    updateSheetImage(v) {
      const img = this.querySelector('.inayah-oc__sheet-image');
      if (!img) return;
      let src = v && v.image;
      if (!src && this.selectedColor) {
        const m = this.variants.find(x => x.image && this.norm(x.color) === this.norm(this.selectedColor));
        src = m && m.image;
      }
      // a11y: the photo now shows the selected colourway, so name it in the alt text too.
      if (this.selectedColor) {
        if (img.dataset.baseAlt === undefined) img.dataset.baseAlt = img.getAttribute('alt') || '';
        img.alt = img.dataset.baseAlt ? img.dataset.baseAlt + ' — ' + this.selectedColor : this.selectedColor;
      }
      if (!src || img.getAttribute('src') === src) return;
      img.classList.add('is-loading');
      img.onload = () => img.classList.remove('is-loading');
      img.onerror = () => img.classList.remove('is-loading');
      img.removeAttribute('srcset');
      img.src = src;
    }

    add() {
      const id = this.addBtn.dataset.variantId;
      if (!id) return;

      // routes/fetchConfig come from global.js; in the (near-impossible) case a
      // click lands before it loads, fail gracefully instead of throwing.
      const cartRoutes = typeof routes !== 'undefined' ? routes : window.routes;
      if (!cartRoutes || !cartRoutes.cart_add_url) {
        this.showError('Cart is still loading — please try again.');
        return;
      }
      const cart = document.querySelector('cart-drawer');

      this.addBtn.setAttribute('disabled', 'disabled');
      this.addBtn.classList.add('is-loading');
      this.hideError();

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', id);
      formData.append('quantity', 1);
      // Mirror the native product form: mark pre-order line items so orders
      // carry the same Pre-order property (theme.preorder metafield).
      var chosen = this.variantFor(this.selectedColor, this.currentSize());
      if (this.dataset.preorder === 'true' || this.isPreorder(chosen)) {
        formData.append('properties[Pre-order]', '✓');
      }
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map(s => s.section).join(','));
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      fetch(`${cartRoutes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          // Shopify returns {status:422,...} on failure (e.g. sold out) and the
          // line item (no status) on success.
          if (response.status || response.errors) {
            this.showError(response.description || response.message || 'Could not add to cart.');
            return;
          }
          if (response.sections && cart && typeof cart.renderContents === 'function') {
            // If we're adding from inside the already-open cart drawer (the upsell),
            // refresh contents WITHOUT re-opening (avoids a focus-trap jump). When the
            // drawer is closed (collection / PDP add), open it to surface the addition.
            var alreadyOpen =
              cart.classList.contains('is-visible') ||
              cart.classList.contains('active') ||
              cart.classList.contains('is--open') ||
              cart.hasAttribute('open');
            try {
              cart.renderContents(response, !alreadyOpen);
            } catch (e) {
              if (!alreadyOpen && typeof cart.open === 'function') cart.open();
            }
          } else if (cart && typeof cart.open === 'function') {
            cart.open();
          }
          if (typeof updateCartCounters === 'function') updateCartCounters();
        })
        .catch(() => this.showError('Could not add to cart.'))
        .finally(() => {
          this.addBtn.classList.remove('is-loading');
          this.updateVariant();
        });
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('inayah-oneclick-add', InayahOneClickAdd);
}

/* ───────────────────────────────────────────────────────────────────────────
   DISSH-style quick-add sheet (delegated; pairs with .inayah-qa-trigger on cards).
   The card shows a clean photo + a small bag icon. Tapping the bag moves that
   card's <inayah-oneclick-add> element to <body> and reveals it as a slide-up
   bottom sheet — appending to <body> lets it escape any transformed ancestor
   (e.g. swiper slides), and moving a node keeps its listeners + its `card`
   reference, so all the existing add-to-cart logic (and the card-swatch colour
   it reads) keeps working untouched.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  if (window.__inayahQAInit) return;
  window.__inayahQAInit = true;

  var backdrop = null;
  var sheet = null; // the inayah-oneclick-add element currently shown as a sheet
  var sheetCard = null; // the card the open sheet belongs to (for driving native swatches)
  var home = null; // { parent, next } — where to put it back on close
  var closeTimer = null;

  function qaNorm(s) {
    return (s == null ? '' : String(s)).toLowerCase().replace(/[\s\-–—]/g, '');
  }

  // Reflect the hidden <select>'s state onto the visible size pills (selected + sold-out).
  function syncPills(oc) {
    var sel = oc.querySelector('.inayah-oc__size');
    var pills = oc.querySelectorAll('.inayah-oc__size-pill');
    if (!sel || !pills.length) return;
    var opts = {};
    Array.prototype.forEach.call(sel.options, function (o) { opts[qaNorm(o.value)] = o; });
    Array.prototype.forEach.call(pills, function (p) {
      var o = opts[qaNorm(p.dataset.size)];
      var soldout = o ? o.disabled : true;
      p.classList.toggle('is-soldout', soldout);
      p.disabled = soldout;
      p.classList.toggle('is-selected', !!o && !soldout && qaNorm(sel.value) === qaNorm(p.dataset.size));
    });
  }

  // The colour currently selected on the card's native swatch.
  function cardColor(oc, card) {
    card = card || sheetCard || oc.card;
    if (!card) return null;
    var known = {};
    oc.querySelectorAll('.inayah-oc__sheet-color').forEach(function (b) { known[qaNorm(b.dataset.color)] = true; });
    var found = null;
    card.querySelectorAll('.variant-option-radio-input').forEach(function (r) {
      if (found) return;
      if ((r.checked || r.hasAttribute('checked')) && known[qaNorm(r.value)]) found = r.value;
    });
    return found;
  }

  // Highlight the sheet swatch matching the card's current colour.
  function syncColors(oc, card) {
    var swatches = oc.querySelectorAll('.inayah-oc__sheet-color');
    if (!swatches.length) return;
    var cur = qaNorm(cardColor(oc, card));
    swatches.forEach(function (b) { b.classList.toggle('is-selected', qaNorm(b.dataset.color) === cur); });
  }

  function syncSheet(oc, card) { syncPills(oc); syncColors(oc, card); }

  function getBackdrop() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'inayah-qa-backdrop';
      backdrop.addEventListener('click', function () { closeSheet(); });
      document.body.appendChild(backdrop);
    }
    return backdrop;
  }

  function openSheet(card) {
    var oc = card.querySelector('inayah-oneclick-add.inayah-oc--hover');
    if (!oc) return;
    if (sheet) closeSheet(true); // instantly retire any already-open sheet
    clearTimeout(closeTimer);

    home = { parent: oc.parentNode, next: oc.nextSibling };
    sheet = oc;
    sheetCard = card;
    document.body.appendChild(oc);
    oc.classList.add('is-sheet');
    syncSheet(oc, card); // reflect current colour + size availability into the pills/swatches

    var bd = getBackdrop();
    bd.style.display = 'block';
    document.documentElement.classList.add('inayah-qa-open');

    // Paint the closed state, then flip to open so the slide-up transition plays. We try
    // requestAnimationFrame for a smooth next-frame flip, but ALSO schedule a setTimeout
    // fallback: some pages (notably the homepage sliders/animation libs) monkey-patch
    // requestAnimationFrame, and when its callback never fires the sheet was left stuck in its
    // off-screen closed state. reveal() is idempotent and guards against a newer sheet.
    var reveal = function () {
      if (sheet !== oc) return; // a newer sheet opened meanwhile
      bd.classList.add('is-open');
      oc.classList.add('is-open');
    };
    requestAnimationFrame(function () {
      requestAnimationFrame(reveal);
    });
    setTimeout(reveal, 60);
    // Safety net: some pages (the homepage's animation scripts) can freeze the slide-up CSS
    // transition, leaving the sheet stuck off-screen at translateY(110%). If it hasn't reached its
    // open position after the transition should have finished, snap it open so the sheet is never
    // left invisible behind the backdrop. Runs AFTER the 0.26s transition window so it never
    // interrupts a working slide.
    setTimeout(function () {
      if (sheet !== oc) return;
      var tr = getComputedStyle(oc).transform;
      var y = tr && tr.indexOf('matrix') === 0 ? parseFloat(tr.split(',')[5]) : 0;
      if (y > 4) {
        oc.style.transition = 'none';
        bd.classList.add('is-open');
        oc.classList.add('is-open');
        void oc.offsetHeight;
        oc.style.transition = '';
      }
    }, 340);
  }

  function closeSheet(instant) {
    if (!sheet) return;
    var oc = sheet;
    var where = home;
    sheet = null;
    sheetCard = null;
    home = null;
    clearTimeout(closeTimer);

    oc.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('inayah-qa-open');

    var restore = function () {
      oc.classList.remove('is-sheet');
      if (where && where.parent) {
        if (where.next && where.next.parentNode === where.parent) {
          where.parent.insertBefore(oc, where.next);
        } else {
          where.parent.appendChild(oc);
        }
      }
      if (backdrop) backdrop.style.display = 'none';
    };
    if (instant) restore();
    else closeTimer = setTimeout(restore, 300);
  }

  // Capture phase: the element's own Add handler calls stopPropagation(), so a
  // bubble-phase document listener would never see the Add click. Capturing lets
  // us observe it (to auto-close on success) without interfering with the add.
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var trigger = t.closest('.inayah-qa-trigger');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        var card = trigger.closest('product-card, .product-card');
        if (card) openSheet(card);
        return;
      }

      if (t.closest('.inayah-oc__sheet-close')) {
        e.preventDefault();
        e.stopPropagation();
        closeSheet();
        return;
      }

      // Size pill → drive the hidden <select> (its change runs the element's existing
      // variant logic), then refresh the pills' selected state.
      var pill = t.closest('.inayah-oc__size-pill');
      if (sheet && pill) {
        e.preventDefault();
        e.stopPropagation();
        if (pill.disabled || pill.classList.contains('is-soldout')) return;
        var selEl = sheet.querySelector('.inayah-oc__size');
        if (selEl) {
          selEl.value = pill.dataset.size;
          selEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        Array.prototype.forEach.call(sheet.querySelectorAll('.inayah-oc__size-pill'), function (p) {
          p.classList.toggle('is-selected', p === pill);
        });
        return;
      }

      // Colour swatch → drive the card's matching native swatch, so card-variant-swap and
      // the element's resync update the variant/image/sizes; then refresh sheet state.
      var sc = t.closest('.inayah-oc__sheet-color');
      if (sheet && sc) {
        e.preventDefault();
        e.stopPropagation();
        var oc2 = sheet;
        // Update the control's colour + image + add-to-bag variant DIRECTLY. These recommendation
        // cards often have no native swatch to proxy, so without this the image/variant never change.
        if (typeof oc2.selectColor === 'function') oc2.selectColor(sc.dataset.color);
        var cardEl = sheetCard || oc2.card;
        if (cardEl) {
          var want = qaNorm(sc.dataset.color);
          var radio = null;
          cardEl.querySelectorAll('.variant-option-radio-input').forEach(function (r) {
            if (!radio && qaNorm(r.value) === want) radio = r;
          });
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('input', { bubbles: true }));
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        Array.prototype.forEach.call(oc2.querySelectorAll('.inayah-oc__sheet-color'), function (b) {
          b.classList.toggle('is-selected', b === sc);
        });
        setTimeout(function () { syncPills(oc2); }, 40); // sizes may change with colour
        return;
      }

      // Add to Bag in the sheet: do NOT stop the event (the element's own handler must
      // run). Poll the add button's loading state — it goes is-loading while the request
      // is in flight and clears it when done. Once it's done, close the sheet on success
      // (so the cart drawer it opened is unobstructed) but leave it open on failure so the
      // shopper sees the error message.
      var addBtn = t.closest('.inayah-oc__add');
      if (sheet && addBtn) {
        var ocEl = sheet;
        var tries = 0;
        var poll = setInterval(function () {
          tries += 1;
          var loading = addBtn.classList.contains('is-loading');
          if (loading && tries < 25) return; // still adding (cap ~2.5s)
          clearInterval(poll);
          if (ocEl !== sheet) return; // a different sheet opened meanwhile
          var err = ocEl.querySelector('.inayah-oc__error');
          var failed = err && !err.hidden && err.textContent.trim();
          if (!failed) closeSheet();
        }, 100);
      }
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet) closeSheet();
  });
})();

/* ───────────────────────────────────────────────────────────────────────────
   One-tap "Also in <other colour>" cross-sell add. Pairs with .inayah-pair-add
   buttons (on cart line items) carrying data-variant-id for the un-owned colourway
   in the line's size. Adds it + refreshes the open cart drawer, reusing the same
   native add path as the one-click control above.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  if (window.__inayahPairAddInit) return;
  window.__inayahPairAddInit = true;

  function pairAdd(id, btn) {
    if (!id) return;
    var cartRoutes = typeof routes !== 'undefined' ? routes : window.routes;
    if (!cartRoutes || !cartRoutes.cart_add_url) return;
    var cart = document.querySelector('cart-drawer');
    if (btn) { btn.setAttribute('disabled', 'disabled'); btn.classList.add('is-loading'); }
    var config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    var formData = new FormData();
    formData.append('id', id);
    formData.append('quantity', 1);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map(function (s) { return s.section; }).join(','));
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;
    fetch(cartRoutes.cart_add_url, config)
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status || response.errors) return;
        if (response.sections && cart && typeof cart.renderContents === 'function') {
          var alreadyOpen =
            cart.classList.contains('is-visible') ||
            cart.classList.contains('active') ||
            cart.classList.contains('is--open') ||
            cart.hasAttribute('open');
          try { cart.renderContents(response, !alreadyOpen); }
          catch (e) { if (!alreadyOpen && typeof cart.open === 'function') cart.open(); }
        } else if (cart && typeof cart.open === 'function') { cart.open(); }
        if (typeof updateCartCounters === 'function') updateCartCounters();
      })
      .catch(function () {})
      .finally(function () { if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('disabled'); } });
  }

  document.addEventListener('click', function (e) {
    var pb = e.target && e.target.closest ? e.target.closest('.inayah-pair-add') : null;
    if (!pb || !pb.dataset.variantId) return;
    e.preventDefault();
    e.stopPropagation();
    pairAdd(pb.dataset.variantId, pb);
  });
})();
